import { inject, Injectable } from '@angular/core';
import {
    DialogConfirmationControl,
    RaDialogConfirmationService,
    RaToastService,
} from '@shared/components';
import { catchError, EMPTY, map, Observable, switchMap } from 'rxjs';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { TrashDocumentItemApi } from '../APIs/trash-document-item.api';
import { TrashDocumentItemEntity } from '../entities/trash-document-item.entity';
import { DomainTranslateService } from '../utils/domain-translate.service';

@Injectable({
    providedIn: 'root',
})
export class TrashDocumentItemController {
    // * [API]
    private _api = inject(TrashDocumentItemApi);

    // * [Injects]
    private _utilService = inject(UtilService);
    private _raToastService = inject(RaToastService);
    private _raDialogConfirmationService = inject(RaDialogConfirmationService);
    private _i18n = inject(DomainTranslateService);

    /**
     * Retorna uma lista de itens da lixeira de um documento
     * @param documentId ID of document
     * @returns Observable of TrashDocumentItemEntity[]
     */
    public getAllByDocumentId(documentId: string): Observable<TrashDocumentItemEntity[]> {
        return this._api.getAllByDocumentId(documentId).pipe(
            map(response => response.body),
            catchError(error =>
                this._utilService.handleError(error, this._i18n.instant('domain.trash.errors.fetch'))
            )
        );
    }

    /**
     * Restaura um item da lixeira de um documento
     * @param documentId ID of document
     * @param _ids IDs of items
     * @returns Observable of void
     */
    public restore(documentId: string, _ids: string[]): Observable<void> {
        return this._api.restore(documentId, _ids).pipe(
            map(response => {
                // Mensagem de sucesso
                this._raToastService.success({
                    message: response.message[0] || this._i18n.instant('domain.trash.success.restored'),
                });

                return response.body;
            }),
            catchError(error =>
                this._utilService.handleError(error, this._i18n.instant('domain.trash.errors.restore'))
            )
        );
    }

    /**
     * Deleta permanentemente um item da lixeira de um documento
     * @param documentId ID of document
     * @param _ids IDs of items
     * @returns Observable of void
     */
    public deletePermanently(documentId: string, ids: string[]): Observable<void> {
        return this._raDialogConfirmationService
            .confirm({
                title: this._i18n.instant('domain.trash.dialog.deletePermanent.title'),
                message: this._i18n.instant('domain.trash.dialog.deletePermanent.message'),
                confirmText: this._i18n.instant('common.confirm'),
                cancelText: this._i18n.instant('common.cancel'),
                type: 'danger',
                irreversible: true,
            })
            .pipe(
                switchMap((dialogControl: DialogConfirmationControl) => {
                    // Ativar loading
                    dialogControl.setLoading(true);

                    return this._api.deletePermanently(documentId, ids).pipe(
                        map(() => {
                            dialogControl.close();
                            return void 0;
                        }),
                        catchError(error => {
                            dialogControl.setLoading(false);
                            this._utilService.handleError(
                                error,
                                this._i18n.instant('domain.trash.errors.deletePermanent')
                            );

                            return EMPTY;
                        })
                    );
                }),
                map(() => {
                    // Mensagem de sucesso
                    this._raToastService.success({
                        message: this._i18n.instant('domain.trash.success.deletedPermanent'),
                    });

                    return void 0;
                })
            );
    }
}
